<template>
  <HeaderComponent/>
  <main class="pt-[70px] w-full h-screen bg-gray-100 relative flex">
    <SideBarComponent/>
    <div class="lg:w-[calc(100%-250px)] md:w-[calc(100%-78px)] lg:left-[250px] md:left-[78px] relative top-0 overflow-y-auto overflow-x-hidden h-full md:p-[25px] p-[20px]">
      <section class="w-full duration-500 relative flex flex-col flex-wrap items-center justify-between mb-[40px]">
        <!-- Header avec bouton d'ajout -->
        <div class="w-full flex justify-between items-center mb-6">
          <h1 class="text-2xl font-bold text-gray-800">Gestion des Administrateurs</h1>
          <button 
            @click="openAddModal"
            class="bg-[#D4AF37] hover:bg-[#C9A227] text-white px-4 py-2 rounded-lg flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
            </svg>
            Ajouter un admin
          </button>
        </div>

        <!-- Tableau des admins -->
        <div class="w-full bg-white rounded-lg shadow overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Poste
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de création
                  </th>
                  <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="admin in admins" :key="admin?._id">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#1f2937]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div class="ml-4">
                        <div class="text-sm font-medium text-gray-900">{{ admin.mail }}</div>
                        <div class="text-sm text-gray-500">{{ getPostLabel(admin.post) }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span 
                      :class="{
                        'bg-green-100 text-green-800': admin.isActive,
                        'bg-red-100 text-red-800': !admin.isActive
                      }" 
                      class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    >
                      {{ admin.isActive ? 'Actif' : 'Inactif' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <select
                      :value="admin.post || ''"
                      class="min-w-[170px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-[#D4AF37] focus:outline-none focus:ring-[#D4AF37]"
                      @change="updateAdminPost(admin, $event.target.value)"
                    >
                      <option
                        v-for="option in adminPostOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {{ formatDate(admin.createdAt) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      @click="toggleAdminStatus(admin?._id)"
                      :class="{
                        'bg-gray-600 hover:bg-gray-700': admin.isActive,
                        'bg-green-600 hover:bg-green-700': !admin.isActive
                      }"
                      class="text-white px-3 py-1 rounded text-sm"
                    >
                      {{ admin.isActive ? 'Désactiver' : 'Activer' }}
                    </button>
                  </td>
                </tr>
                <tr v-if="admins.length === 0">
                  <td colspan="5" class="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun administrateur trouvé
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Modal d'ajout -->
        <div v-if="showAddModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium text-gray-900">Ajouter un administrateur</h3>
              <button @click="closeAddModal" class="text-gray-400 hover:text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form @submit.prevent="submitAdmin">
              <div class="mb-4">
                <label for="email" class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input 
                  v-model="newAdmin.mail"
                  type="email" 
                  id="email" 
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                >
              </div>
              
              <div class="mb-4">
                <label for="password" class="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                <input 
                  v-model="newAdmin.password"
                  type="password" 
                  id="password" 
                  required
                  minlength="6"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                >
              </div>

              <div class="mb-4">
                <label for="post" class="block text-sm font-medium text-gray-700 mb-1">Poste</label>
                <select
                  id="post"
                  v-model="newAdmin.post"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                >
                  <option
                    v-for="option in adminPostOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>
              
              <div class="flex justify-end space-x-3 pt-4">
                <button 
                  type="button" 
                  @click="closeAddModal"
                  class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#D4AF37] hover:bg-[#C9A227] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4AF37]"
                >
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import HeaderComponent from '@/components/HeaderComponentAdmin.vue'
import SideBarComponent from '@/components/SideBarComponent.vue'
import apiClient from '@/services/api'
import { toast } from 'vue3-toastify'

const admins = ref([])
const showAddModal = ref(false)
const adminPostOptions = [
  { value: '', label: 'Aucun poste' },
  { value: 'service_client', label: 'Service client' }
]
const newAdmin = ref({
  mail: '',
  password: '',
  post: ''
})

const getPostLabel = (post) => {
  return adminPostOptions.find((option) => option.value === (post || ''))?.label || 'Aucun poste'
}

// Formatage de la date
const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' }
  return new Date(dateString).toLocaleDateString('fr-FR', options)
}

// Charger la liste des admins
const loadAdmins = async () => {
  try {
    const response = await apiClient.get('/auth/admins')
    admins.value = response.data
  } catch (error) {
    toast.error('Une erreur est survenue lors du chargement des administrateurs', {
      position: 'top-right',
      autoClose: 3000
    });
  }
}

// Ouvrir le modal d'ajout
const openAddModal = () => {
  showAddModal.value = true
}

// Fermer le modal d'ajout
const closeAddModal = () => {
  showAddModal.value = false
  newAdmin.value = { mail: '', password: '', post: '' }
}

// Soumettre un nouvel admin
const submitAdmin = async () => {
  try {
    const response = await apiClient.post('/auth/admins', newAdmin.value)
    closeAddModal()
    loadAdmins()
    toast.success('Administrateur créé avec succès', {
      position: 'top-right',
      autoClose: 3000
    });
    alert('Administrateur créé avec succès')
  } catch (error) {
    toast.error(error.response?.data?.message || 'Une erreur est survenue lors de la création', {
      position: 'top-right',
      autoClose: 3000
    });
  }
}

const updateAdminPost = async (admin, post) => {
  const previousPost = admin.post || ''
  admin.post = post

  try {
    const response = await apiClient.patch(`/auth/admins/${admin._id}/post`, { post })
    admin.post = response.data.admin.post || ''
    toast.success('Poste mis à jour avec succès', {
      position: 'top-right',
      autoClose: 2500
    })
  } catch (error) {
    admin.post = previousPost
    toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour du poste', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}

// Changer le statut d'un admin
const toggleAdminStatus = async (adminId) => {
  if (!confirm('Voulez-vous vraiment modifier le statut de cet administrateur ?')) return
  
  try {
    const response = await apiClient.patch(`/auth/admins/${adminId}/toggle`)
    const index = admins.value.findIndex(a => a?._id === adminId)
    if (index !== -1) {
      admins.value[index].isActive = response.data.admin.isActive
    }
    loadAdmins()
  } catch (error) {
    console.error('Erreur lors du changement de statut:', error)
    alert('Une erreur est survenue lors du changement de statut')
  }
}

// Charger les données au montage du composant
onMounted(() => {
  loadAdmins()
})
</script>

<style>
/* Styles personnalisés si nécessaire */
</style>
